import { useState } from "react";
import TableRow from "@mui/material/TableRow";
import IconButton from "@mui/material/IconButton";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import MetaAdGroupRow from "./MetaAdGroupRow";
import { useFormContext } from "react-hook-form";
import { getFromStatisticsAdsCampaign } from "../../../../../services/adsCampaigns";
import { META } from "../../../../../utils/constants";
import {
  VALUE_NUMBER,
  VALUE_WITH_CURRENCY_MANUAL_FORMAT,
  VALUE_WITH_PERCENTAGE,
} from "../../../../../i18n/keysTranslations";
import { useTranslationApp } from "../../../../../lib/i18next";
import TableCell from "../../../../BillingAndPayments/components/TableCell";
import { expanderColumnSx, metricColumnSx } from "../columnWidths";
import Box from "@mui/material/Box";
import Avatar from "@mui/material/Avatar";
import { logoPlatform } from "../../../../../utils/string";
import Typography from "@mui/material/Typography";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import { renderMetricValue } from "../../../../../utils/renderMetricValue";
import {
  resolveCampaignLevelStatistics,
  resolveStatisticsMap,
} from "../../../../NewAdsCampaign/components/Common/campaignLevelMetrics";

const MetaCampaignRow = ({ index, sortConfig, columns }) => {
  const [open, setOpen] = useState(false);
  const { campaign, getCostByMainCurrency } = useFormContext();
  const { statistics } = getFromStatisticsAdsCampaign(campaign);

  const { t } = useTranslationApp();

  const conversionActions = campaign?.platformsData?.[META]?.conversionActions;
  const statisticsPlatform = resolveCampaignLevelStatistics({
    raw: statistics[META]?.campaign || {},
    platform: META,
    conversionActions,
  }).statistics;
  const adsGroupStatisticsById = resolveStatisticsMap(
    statistics[META]?.adsGroups,
    META,
    conversionActions
  );
  const adStatisticsById = resolveStatisticsMap(
    statistics[META]?.ads,
    META,
    conversionActions
  );
  const adsGroups = campaign?.adsGroups || [];
  const adsGroupsMeta = adsGroups.filter(
    (adsGroup) => adsGroup.platform === META
  );

  // Sort ad groups
  const sortedAdsGroups = [...adsGroupsMeta].sort((a, b) => {
    const aStats = adsGroupStatisticsById[a.id];
    const bStats = adsGroupStatisticsById[b.id];

    if (!aStats || !bStats) return 0;

    if (sortConfig.key) {
      const comparison =
        (aStats[sortConfig.key] || 0) - (bStats[sortConfig.key] || 0);
      return sortConfig.direction === "asc" ? comparison : -comparison;
    }
    return 0;
  });

  const { cost, currency } = getCostByMainCurrency({
    cost: statisticsPlatform.spend,
    currency: statisticsPlatform.currency,
  });

  const { cost: costPerMainAction, currency: currencyPerMainAction } =
    getCostByMainCurrency({
      cost: statisticsPlatform.costPerMainAction,
      currency: statisticsPlatform.currency,
    });

  // Mapping to render each cell according to the column
  const cellRenderers = {
    platform: () => (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
          gap: 1,
          height: "100%",
          width: "100%",
        }}
      >
        <Avatar
          data-hide-in-pdf-capture
          src={logoPlatform(META)}
          variant="square"
          sx={{
            width: 20,
            height: 20,
            ".MuiAvatar-img": { objectFit: "contain" },
          }}
        />
        <Typography variant="body2" sx={{ mt: 0.25 }}>
          {t(META)}
        </Typography>
      </Box>
    ),
    spend: () =>
      t(VALUE_WITH_CURRENCY_MANUAL_FORMAT, { value: cost, currency }),
    impressions: () =>
      t(VALUE_NUMBER, { value: statisticsPlatform.impressions }),
    reach: () => t(VALUE_NUMBER, { value: statisticsPlatform.reach }),
    click: () => t(VALUE_NUMBER, { value: statisticsPlatform.click }),
    ctr: () => t(VALUE_WITH_PERCENTAGE, { value: statisticsPlatform.ctr }),
    mainAction: () => t(VALUE_NUMBER, { value: statisticsPlatform.mainAction }),
    costPerMainAction: () =>
      renderMetricValue(
        costPerMainAction,
        "costPerMainAction",
        currencyPerMainAction
      ),
    cpc: () =>
      t(VALUE_WITH_CURRENCY_MANUAL_FORMAT, {
        value: (statisticsPlatform.cpc || 0).toFixed(2),
        currency: statisticsPlatform.currency,
      }),
    cpm: () =>
      t(VALUE_WITH_CURRENCY_MANUAL_FORMAT, {
        value: (statisticsPlatform.cpm || 0).toFixed(2),
        currency: statisticsPlatform.currency,
      }),
    // Here you can add custom renderers for specific metrics
  };

  return (
    <>
      <TableRow>
        <TableCell indexRow={index} sx={expanderColumnSx} level={1}>
          <IconButton size="small" onClick={() => setOpen(!open)}>
            {open ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
          </IconButton>
        </TableCell>
        {columns.map((col) => (
          <TableCell
            key={col.key}
            indexRow={index}
            level={1}
            sx={metricColumnSx(col.width)}
          >
            {cellRenderers[col.key]
              ? cellRenderers[col.key]()
              : renderMetricValue(
                  statisticsPlatform[col.key],
                  col.key,
                  statisticsPlatform.currency
                )}
          </TableCell>
        ))}
      </TableRow>
      {open &&
        sortedAdsGroups.map((adGroup, indexAdGroup) => {
          const adGroupID = adGroup.id;
          const adGroupStatistics = adsGroupStatisticsById[adGroupID];

          if (!adGroupStatistics) return null;

          const adsStatistics = {};

          for (const ad of adGroup.ads) {
            const adID = ad.id;
            const adStatistics = adStatisticsById[adID];

            if (!adStatistics) continue;

            adsStatistics[adID] = adStatistics;
          }

          return (
            <MetaAdGroupRow
              indexCampaign={index}
              adGroupID={adGroup.id}
              indexAdGroup={indexAdGroup}
              key={adGroup.id}
              data={adGroupStatistics}
              adsStatistics={adsStatistics}
              sortConfig={sortConfig}
              columns={columns}
            />
          );
        })}
    </>
  );
};

export default MetaCampaignRow;
